import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Typography,
} from "@mui/material";
import {
  AutoAwesome,
  Block as BlockIcon,
  Check as CheckIcon,
  Close,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Replay,
  TaskAlt,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import activityService from "../features/activity/activityService";
import { diffSnapshot, parseSnapshot } from "./flagReviewUtils";

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("en-GB");
};

const formatDateTime = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("en-GB");
};

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

// Purple/pink gradient badge for flags created by the AI (chat assistant
// or the daily automated triage).
export function AiFlagBadge() {
  return (
    <Chip
      size="small"
      label="Tagged by AI"
      icon={<AutoAwesome sx={{ fontSize: 12 }} />}
      sx={{
        background: "linear-gradient(135deg, #a855f7, #ec4899)",
        color: "#ffffff",
        fontWeight: 700,
        height: 20,
        "& .MuiChip-icon": { color: "#ffffff" },
        "& .MuiChip-label": { fontSize: "0.65rem", px: 0.5 },
      }}
    />
  );
}

const statusColor = (status) =>
  status === "resolved" ? "success" : status === "reviewed" ? "warning" : "default";

function SectionTitle({ children }) {
  return (
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}
    >
      {children}
    </Typography>
  );
}

function LabelValue({ label, value, children }) {
  return (
    <Typography
      variant="caption"
      sx={{ color: "text.primary", display: "block" }}
    >
      <strong>{label}:</strong> {children ?? value ?? "—"}
    </Typography>
  );
}

function ValidityChip({ entry }) {
  if (entry.is_valid === false) {
    return (
      <Chip
        label={`Invalid: ${entry.invalid_reason || "no reason recorded"}`}
        color="error"
        size="small"
        variant="outlined"
      />
    );
  }
  if (entry.is_valid === true) {
    return (
      <Chip label="Valid" color="success" size="small" variant="outlined" />
    );
  }
  return <Chip label="Not reviewed" size="small" variant="outlined" />;
}

function PointsBreakdown({ entry }) {
  let breakdown = entry.points_description;
  if (typeof breakdown === "string") {
    try {
      breakdown = JSON.parse(breakdown);
    } catch {
      breakdown = null;
    }
  }
  const steps = breakdown?.breakdown || [];
  if (steps.length === 0) return null;

  return (
    <Typography
      variant="caption"
      sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
    >
      <strong>Points breakdown:</strong>{" "}
      {steps.map((s) => `${s.name || s.step} ${s.allocation}%`).join(" · ")}
      {breakdown?.basePoints != null ? ` — base ${breakdown.basePoints}` : ""}
      {breakdown?.totalPoints != null
        ? ` — total ${breakdown.totalPoints}`
        : ""}
    </Typography>
  );
}

function EntryCard({
  entry,
  onMarkValid,
  onMarkInvalid,
  onResend,
  onDeleteEntry,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, backgroundColor: "background.default" }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {entry.event}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {formatDate(entry.event_date)} ·{" "}
        {entry.position_name || entry.position || "—"} ·{" "}
        {entry.district || "—"} · {entry.points || 0} pts
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1,
          mt: 1.5,
        }}
      >
        <LabelValue label="ID" value={entry.id} />
        <LabelValue
          label="Category"
          value={entry.category_name || entry.category || "—"}
        />
        <LabelValue
          label="Format"
          value={entry.format_name || entry.format || "—"}
        />
        <LabelValue
          label="Level"
          value={entry.level_name || entry.level || "—"}
        />
        <LabelValue
          label="Player"
          value={`${entry.first_name || entry.player_name || "—"} ${
            entry.last_name || ""
          }${entry.bsa_number ? ` (BSA ${entry.bsa_number})` : ""}`}
        />
        <LabelValue label="Category Type" value={entry.category_type || "—"} />
        <LabelValue
          label="Tier"
          value={entry.tier_name || entry.tier || "—"}
        />
        <LabelValue
          label="Junior Age Group"
          value={entry.junior_age_group || "—"}
        />
        <LabelValue label="Gender" value={entry.gender || "—"} />
        <LabelValue label="Province" value={entry.province || "—"} />
        <LabelValue label="Created" value={formatDate(entry.created_at)} />
        <LabelValue label="Updated" value={formatDateTime(entry.updated_at)} />
        <LabelValue label="Updated by" value={entry.updated_by_name || "—"} />
      </Box>
      {entry.marked_invalid_at && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
        >
          <strong>Marked invalid:</strong>{" "}
          {formatDateTime(entry.marked_invalid_at)}
          {entry.marked_invalid_by ? ` by ${entry.marked_invalid_by}` : ""}
        </Typography>
      )}
      <PointsBreakdown entry={entry} />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          mt: 1.5,
        }}
      >
        <ValidityChip entry={entry} />
        {entry.is_valid === false && (
          <>
            <Chip
              label="Edit invalid message"
              size="small"
              icon={<EditIcon sx={{ fontSize: 14 }} />}
              onClick={() => onMarkInvalid(entry)}
              title="Edit invalid message"
              sx={{ cursor: "pointer" }}
            />
            <Button
              size="small"
              variant="text"
              startIcon={<EmailIcon />}
              onClick={() => onResend(entry)}
              title="Re-send the player notification for this entry"
            >
              Resend email
            </Button>
          </>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
        {(entry.is_valid === null || entry.is_valid === false) && (
          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={<CheckIcon />}
            onClick={() => onMarkValid(entry.id)}
          >
            Mark Valid
          </Button>
        )}
        {(entry.is_valid === null || entry.is_valid === true) && (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<BlockIcon />}
            onClick={() => onMarkInvalid(entry)}
          >
            Mark Invalid
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => onDeleteEntry(entry)}
        >
          Delete
        </Button>
      </Box>
    </Paper>
  );
}

function CandidateCard({ candidate, isAiTwin, hasOpenFlag }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          mb: 0.5,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Entry #{candidate.id}
        </Typography>
        {isAiTwin && (
          <Chip
            size="small"
            label="AI comparison"
            color="secondary"
            variant="outlined"
          />
        )}
        {hasOpenFlag && (
          <Chip size="small" label="Has open flag" color="warning" />
        )}
        <ValidityChip entry={candidate} />
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block" }}
      >
        {candidate.event}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block" }}
      >
        {formatDate(candidate.event_date)} ·{" "}
        {candidate.position_name || candidate.position || "—"} ·{" "}
        {candidate.category_name || candidate.category || "—"} ·{" "}
        {candidate.format_name || candidate.format || "—"} ·{" "}
        {candidate.level_name || candidate.level || "—"} ·{" "}
        {candidate.tier_name || candidate.tier || "—"} ·{" "}
        {candidate.points ?? 0} pts
      </Typography>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

export default function FlagReviewDialog({
  flag,
  flagsWithEntries = [],
  openFlaggedEntryIds,
  updating = false,
  onClose,
  onSelectFlag,
  onResolve,
  onReopen,
  onMarkValid,
  onMarkInvalid,
  onResend,
  onDeleteEntry,
  onDeleteFlag,
}) {
  const [history, setHistory] = useState([]);
  const [historyState, setHistoryState] = useState("idle");

  const flagId = flag?.id;
  const status = flag?.status || "pending";
  // Prefer the entry the flag is actually linked to; fallback matches are
  // display-only (the server excludes rankings by entry_id alone).
  const primaryEntry =
    (flag?.entry_id != null &&
      flag?.entries?.find(
        (candidate) => String(candidate.id) === String(flag.entry_id),
      )) ||
    flag?.entries?.[0] ||
    null;

  // The trail changes whenever the flag moves or its entry's validity does,
  // so refetch on those signals too (the page refetches after every action).
  const historyKey = [
    flagId,
    status,
    flag?.resolved_at || "",
    flag?.player_reviewed_at || "",
    primaryEntry?.is_valid,
  ].join("|");

  useEffect(() => {
    if (!flagId) return undefined;

    let cancelled = false;
    setHistory([]);
    setHistoryState("loading");

    activityService
      .getEntityActivities("flag", flagId)
      .then((response) => {
        if (cancelled) return;
        setHistory(Array.isArray(response) ? response : response?.data || []);
        setHistoryState("done");
      })
      .catch(() => {
        if (cancelled) return;
        setHistoryState("error");
      });

    return () => {
      cancelled = true;
    };
    // historyKey folds in the flag id and the state changes that add rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyKey]);

  if (!flag) return null;

  const entry = primaryEntry;

  // Other open flags on the same entry change what resolving this one does.
  const relatedFlags = flagsWithEntries.filter((other) => {
    if (String(other.id) === String(flag.id)) return false;
    if (flag.entry_id && other.entry_id) {
      return String(flag.entry_id) === String(other.entry_id);
    }
    const entryId = flag.entries?.[0]?.id;
    return (
      entryId != null &&
      (other.entries || []).some(
        (otherEntry) => String(otherEntry.id) === String(entryId),
      )
    );
  });
  const openRelatedCount = relatedFlags.filter(
    (other) =>
      (other.status || "pending") !== "resolved" &&
      // Only entry_id flags affect the rankings. A legacy flag matched by
      // event/date is shown for context but excludes nothing.
      flag.entry_id != null &&
      other.entry_id != null &&
      String(other.entry_id) === String(flag.entry_id),
  ).length;

  // AI twin is always shown, even when it falls outside the ±3-day window.
  const candidates = [...(flag.duplicate_candidates || [])];
  if (flag.ai_twin_entry) {
    const twin = flag.ai_twin_entry;
    const alreadyListed = candidates.some(
      (candidate) => String(candidate.id) === String(twin.id),
    );
    if (!alreadyListed) candidates.unshift(twin);
  }
  const visibleCandidates = candidates.filter(
    (candidate) => String(candidate.id) !== String(entry?.id),
  );
  const twinId = flag.ai_twin_entry?.id;

  const snapshot = parseSnapshot(flag.entry_snapshot);
  const changes = diffSnapshot(snapshot, entry);

  let banner;
  if (flag.entry_id == null) {
    banner = {
      severity: "info",
      text: "This flag is not linked to an entry, so it does not exclude anything from the rankings. Resolving it will not change the standings.",
    };
  } else if (!entry) {
    banner = {
      severity: "warning",
      text: "The entry this flag is linked to no longer exists. Nothing is excluded from the rankings and the flag is stale - deleting it keeps the queue clean.",
    };
  } else if (entry.is_valid === false) {
    banner = {
      severity: "warning",
      text: "This entry is marked invalid, so it is already excluded from the rankings. Resolving this flag will not bring it back.",
    };
  } else if (status === "resolved") {
    banner = {
      severity: "success",
      text:
        openRelatedCount > 0
          ? `This flag no longer excludes the entry, but ${openRelatedCount} other open flag(s) on it still do.`
          : "This flag is resolved: the entry is included in the rankings.",
    };
  } else {
    banner = {
      severity: "warning",
      text:
        openRelatedCount > 0
          ? `Until this flag is resolved the entry is excluded from the rankings. It also has ${openRelatedCount} other open flag(s), so resolving this one alone will not bring it back.`
          : "Until this flag is resolved, the entry is excluded from the rankings.",
    };
  }

  return (
    <Dialog open maxWidth="lg" fullWidth onClose={onClose}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Flag #{flag.id}
            </Typography>
            {flag.source === "ai" && <AiFlagBadge />}
            <Chip
              label={status}
              color={statusColor(status)}
              size="small"
              sx={{ textTransform: "capitalize" }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" noWrap>
            {flag.player_name} · {flag.event_name}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close flag details">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity={banner.severity} variant="outlined" sx={{ mb: 3 }}>
          {banner.text}
        </Alert>

        <Box sx={{ mb: 3 }}>
          <SectionTitle>Flag</SectionTitle>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            {flag.reason}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1,
            }}
          >
            <LabelValue label="Created" value={formatDateTime(flag.created_at)} />
            <LabelValue
              label="Resolved"
              value={flag.resolved_at ? formatDateTime(flag.resolved_at) : "No"}
            />
            <LabelValue
              label="Player reviewed"
              value={
                flag.player_reviewed_at
                  ? formatDateTime(flag.player_reviewed_at)
                  : status === "resolved"
                    ? "No"
                    : "Not yet"
              }
            />
            <LabelValue
              label="Flagged by"
              value={flag.user_email || flag.user_id || "Anonymous"}
            />
            <LabelValue
              label="Source"
              value={flag.source === "ai" ? "AI" : "User"}
            />
          </Box>
          {flag.extra_info && (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 1, color: "text.primary" }}
            >
              <strong>Additional info:</strong> {flag.extra_info}
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 3 }}>
          <SectionTitle>The entry</SectionTitle>
          {flag.entries && flag.entries.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {flag.entries.map((matchedEntry) => (
                <EntryCard
                  key={`entry-${matchedEntry.id}`}
                  entry={matchedEntry}
                  onMarkValid={onMarkValid}
                  onMarkInvalid={onMarkInvalid}
                  onResend={onResend}
                  onDeleteEntry={onDeleteEntry}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No matching entries found for this flag.
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 3 }}>
          <SectionTitle>Possible duplicates</SectionTitle>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            Same player, same event name, within 3 days - the rule the AI
            triage uses.
          </Typography>
          {visibleCandidates.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {visibleCandidates.map((candidate) => (
                <CandidateCard
                  key={`candidate-${candidate.id}`}
                  candidate={candidate}
                  isAiTwin={String(candidate.id) === String(twinId)}
                  hasOpenFlag={openFlaggedEntryIds?.has(
                    String(candidate.id),
                  )}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No other entries for this player at this event.
            </Typography>
          )}
        </Box>

        {relatedFlags.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <SectionTitle>Other flags on this entry</SectionTitle>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {relatedFlags.map((other) => (
                <Paper
                  key={`related-${other.id}`}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", fontWeight: 600 }}
                    >
                      #{other.id} · {other.reason}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(other.created_at)} ·{" "}
                      {other.user_email || "Anonymous"}
                      {other.source === "ai" ? " · AI" : ""}
                    </Typography>
                  </Box>
                  <Chip
                    label={other.status || "pending"}
                    color={statusColor(other.status || "pending")}
                    size="small"
                    sx={{ textTransform: "capitalize" }}
                  />
                  <Button size="small" onClick={() => onSelectFlag(other.id)}>
                    View
                  </Button>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {snapshot && entry && (
          <Box sx={{ mb: 3 }}>
            <SectionTitle>Changes since flagged</SectionTitle>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Compared with the last stored baseline. The baseline is refreshed
              after a change notification or when a flag is reopened.
            </Typography>
            {flag.change_notified_at && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Last change notification:{" "}
                {formatDateTime(flag.change_notified_at)}
              </Typography>
            )}
            {changes.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {changes.map((change) => (
                  <Typography
                    key={change.label}
                    variant="caption"
                    sx={{ color: "text.primary" }}
                  >
                    <strong>{change.label}:</strong> {change.from} →{" "}
                    <strong>{change.to}</strong>
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Unchanged since the flag was created.
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <SectionTitle>Player</SectionTitle>
          {flag.traced_player ? (
            <Typography variant="caption" sx={{ color: "text.primary" }}>
              {flag.traced_player.firstName} {flag.traced_player.lastName}{" "}
              (BSA-{flag.traced_player.bsa_number}) —{" "}
              {flag.traced_player.email || "no email on file"}{" "}
              <Link
                component={RouterLink}
                to={`/players/${flag.traced_player.bsa_number}`}
              >
                View profile
              </Link>
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {flag.entry_id
                ? "Linked entry - player trace unavailable."
                : "No player traced to this flag."}
            </Typography>
          )}
        </Box>

        <Box>
          <SectionTitle>History</SectionTitle>
          {historyState === "loading" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Loading activity…
              </Typography>
            </Box>
          )}
          {historyState === "error" && (
            <Typography variant="caption" color="error">
              Could not load the flag history.
            </Typography>
          )}
          {historyState === "done" && history.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No activity recorded for this flag.
            </Typography>
          )}
          {historyState === "done" && history.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {history.map((row) => (
                <Box key={`history-${row.id}`}>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", fontWeight: 600 }}
                  >
                    {row.description || row.action_type}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(row.created_at)} ·{" "}
                    {row.user_name || row.user_email || "System / AI"} ·{" "}
                    {row.action_type}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, gap: 1, justifyContent: "space-between" }}
      >
        <Button
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => onDeleteFlag(flag)}
        >
          Delete Flag
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          {status === "resolved" ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Replay />}
              disabled={updating}
              onClick={() => onReopen(flag)}
            >
              Reopen
            </Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<TaskAlt />}
              disabled={updating}
              onClick={() => onResolve(flag)}
            >
              Mark Resolved
            </Button>
          )}
          <Button size="small" variant="outlined" onClick={onClose}>
            Close
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
